import { useState } from 'react'
import { format, parseISO } from 'date-fns'
import { Loader2 } from 'lucide-react'
import { useCalendarGrid } from '@/hooks/useCalendarGrid'
import { updateMilestone } from '@/api/calendars'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from '@/stores/useToastStore'
import { getErrorMessage } from '@/utils/getErrorMessage'
import { invalidateCalendarQueries } from '@/hooks/useCalendarQueryHelpers'
import { cn } from '@/utils/cn'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { Calendar } from '@/components/ui/calendar'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import type { CalendarGridCell, CalendarGridRow } from '@/types'

interface CalendarGridViewProps {
  calendarId: string
}

function EditableDateCell({
  cell,
  row,
  calendarId,
  onSaved,
}: {
  cell: CalendarGridCell
  row: CalendarGridRow
  calendarId: string
  onSaved: () => void
}) {
  const [open, setOpen] = useState(false)
  const [saving, setSaving] = useState(false)

  const dateLabel = cell.resolved_date
    ? format(parseISO(cell.resolved_date), 'MMM d, yyyy')
    : '--'

  const selectedDate = cell.resolved_date
    ? parseISO(cell.resolved_date)
    : undefined

  const handleDateSelect = async (day: Date | undefined) => {
    if (!day) return
    const newDate = format(day, 'yyyy-MM-dd')
    if (newDate === cell.resolved_date) {
      setOpen(false)
      return
    }
    setOpen(false)
    setSaving(true)
    try {
      await updateMilestone(calendarId, row.event_id, cell.milestone_id, {
        specific_date: newDate,
      })
      onSaved()
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to update date'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          className="flex flex-col items-center gap-0.5 cursor-pointer disabled:cursor-wait"
          disabled={saving}
        >
          {saving ? (
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          ) : (
            <>
              <span className="font-medium">{dateLabel}</span>
              {cell.fiscal_week_label && (
                <span className="text-xs text-muted-foreground">
                  {cell.fiscal_week_label}
                </span>
              )}
            </>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="center">
        <Calendar
          mode="single"
          selected={selectedDate}
          defaultMonth={selectedDate}
          onSelect={handleDateSelect}
        />
      </PopoverContent>
    </Popover>
  )
}

function ReadOnlyCell({ cell }: { cell: CalendarGridCell }) {
  const dateLabel = cell.resolved_date
    ? format(parseISO(cell.resolved_date), 'MMM d, yyyy')
    : '--'

  return (
    <div className="flex flex-col items-center gap-0.5">
      <span className="font-medium">{dateLabel}</span>
      {cell.fiscal_week_label && (
        <span className="text-xs text-muted-foreground">
          {cell.fiscal_week_label}
        </span>
      )}
    </div>
  )
}

export function CalendarGridView({ calendarId }: CalendarGridViewProps) {
  const queryClient = useQueryClient()
  const { data, isLoading, isError, refetch } = useCalendarGrid(calendarId)

  const handleCellSaved = () => {
    void refetch()
    invalidateCalendarQueries(queryClient, calendarId)
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (isError) {
    return (
      <div className="flex items-center justify-center py-12 text-sm text-destructive">
        Failed to load calendar grid.
      </div>
    )
  }

  if (!data || data.columns.length === 0 || data.rows.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
        <p className="text-sm">
          No grid data available. Add events and milestones first.
        </p>
      </div>
    )
  }

  const { columns, rows } = data

  return (
    <TooltipProvider>
      <div className="overflow-x-auto border-[#e7e7e7]">
        <table className="w-full border-collapse text-sm text-[#1c1c1c]">
          <thead>
            <tr className="bg-[#f9fafb] border-b border-[#e7e7e7]">
              <th className="sticky left-0 z-10 min-w-[160px] border-r border-[#e7e7e7] bg-[#f9fafb] px-4 py-3 text-left font-semibold text-[#101828]">
                Event
              </th>
              {columns.map(col => (
                <th
                  key={col.column_index}
                  className="min-w-[120px] border-r border-[#e7e7e7] px-4 py-3 text-center font-semibold text-[#101828] last:border-r-0"
                >
                  {col.name}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map(row => {
              const cellMap = new Map(row.cells.map(c => [c.column_index, c]))
              return (
                <tr
                  key={row.event_id}
                  className="border-b border-[#e7e7e7] last:border-b-0 transition-colors hover:bg-[hsl(220_13%_91%/0.5)]"
                >
                  <td className="sticky left-0 z-10 border-r border-[#e7e7e7] bg-white px-4 py-3 font-medium">
                    {row.event_name}
                  </td>
                  {columns.map(col => {
                    const cell = cellMap.get(col.column_index)
                    const isCalculated = cell?.date_mode === 'CALCULATED'
                    return (
                      <td
                        key={col.column_index}
                        className={cn(
                          'border-r border-[#e7e7e7] px-4 py-3 text-center last:border-r-0',
                          cell && !cell.is_editable
                            ? 'bg-[#f3f4f6]'
                            : 'bg-white'
                        )}
                      >
                        {!cell ? (
                          <span className="text-muted-foreground">-</span>
                        ) : cell.is_editable ? (
                          <EditableDateCell
                            cell={cell}
                            row={row}
                            calendarId={calendarId}
                            onSaved={handleCellSaved}
                          />
                        ) : isCalculated ? (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div>
                                <ReadOnlyCell cell={cell} />
                              </div>
                            </TooltipTrigger>
                            <TooltipContent>
                              This date is calculated from another milestone
                            </TooltipContent>
                          </Tooltip>
                        ) : (
                          <ReadOnlyCell cell={cell} />
                        )}
                      </td>
                    )
                  })}
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </TooltipProvider>
  )
}
